import { Link } from "react-router-dom";
import { SeoPageShell } from "./SeoPageShell";

const FAQS = [
  ["How long should a firefighter resume be?", "One page is the usual target for entry-level candidates. If you have extensive relevant experience — for example years as a paramedic or in the military — a second page can be justified, but keep it tight and relevant."],
  ["Should I include certifications that are not yet complete?", "You can list a certification as in progress with the expected completion date, clearly marked. Never list a certification you do not hold as if it were complete."],
  ["Do I need a resume if the department uses an online application?", "Often yes. Many departments ask for a resume at the interview or background stage even when the initial application is a form, and having one ready makes filling out applications faster and more consistent."],
  ["Can I build a firefighter resume for free on First Step?", "Yes. A free account includes the Resume Builder — type or dictate your background, edit sections and preview five templates. PDF download and the AI resume review are part of Premium."],
];

export default function FirefighterResumeExamples() {
  return (
    <SeoPageShell
      path="/firefighter-resume-examples"
      title="Firefighter Resume Examples: What to Include & Common Mistakes"
      description="What to include on a firefighter resume — experience, EMT and fire certifications, education, skills and volunteer work — with example bullet points and the mistakes to avoid."
      h1="Firefighter Resume Examples"
      intro="A firefighter resume has one job: show a hiring panel, quickly, that you are qualified, dependable and prepared. This guide covers what to include in each section, example wording you can adapt, and the mistakes that most often weaken otherwise good candidates."
      faqs={FAQS}
      testId="seo-resume-examples-page"
      cta="Create a free account to build your fire-service resume in First Step's Resume Builder — type or dictate your background and preview five clean templates. PDF download and AI review are part of Premium."
    >
      <section>
        <h2>What a firefighter resume needs to do</h2>
        <p>Fire department hiring is competitive and structured. Your resume is usually read quickly, often alongside an application form and a background packet. It should make three things obvious in a few seconds: that you meet the minimum qualifications, that you hold or are pursuing the relevant certifications, and that your work history shows reliability, teamwork and service. Everything else supports those points.</p>
      </section>

      <section>
        <h2>Sections to include</h2>

        <h3>Header and contact information</h3>
        <p>Full name, phone number, a professional email address, and city and state. A mailing address is optional unless the application asks for it. Skip photos, logos and personal details such as age or marital status.</p>

        <h3>Professional summary (two or three lines)</h3>
        <p>A short statement of who you are and what you bring. Be concrete and avoid clichés.</p>
        <ul>
          <li><em>Example:</em> "EMT with two years of 911 transport experience and Firefighter I certification, seeking an entry-level firefighter position. Known for calm patient care, dependable teamwork and consistent attendance."</li>
        </ul>

        <h3>Certifications and licenses</h3>
        <p>List these prominently — many panels look here first. Include the credential name, issuing body, and expiration or completion date. Common items candidates list include:</p>
        <ul>
          <li>EMT or Paramedic certification/license (state and, where applicable, national registry).</li>
          <li>Firefighter I / Firefighter II, and the certifying body.</li>
          <li>Physical ability test card (for example, CPAT) if the department accepts one and yours is current.</li>
          <li>CPR/BLS, hazardous materials awareness or operations, wildland certifications, driver's licenses and endorsements.</li>
        </ul>
        <p>Only list what you actually hold; mark anything in progress clearly. Requirements differ by department, so check each department's announcement for what they require or prefer.</p>

        <h3>Experience</h3>
        <p>List positions in reverse chronological order with employer, title, location and dates. Use two to four bullet points per role that describe what you did and, where possible, the result. Fire, EMS, military, public-safety and trades experience are directly relevant; retail, food service and warehouse work still count when the bullets show responsibility, teamwork and customer service.</p>
        <ul>
          <li><em>Weak:</em> "Responsible for patient care."</li>
          <li><em>Stronger:</em> "Provided BLS patient care and transport on a two-person 911 ambulance, averaging multiple calls per shift and completing accurate patient care reports."</li>
          <li><em>Weak:</em> "Worked at a warehouse."</li>
          <li><em>Stronger:</em> "Operated forklifts and loaded trucks on a six-person night crew; maintained a clean safety record over three years and trained new hires on lockout procedures."</li>
        </ul>

        <h3>Education</h3>
        <p>Degrees, fire academy, EMT program and relevant college coursework, with the institution and completion date. If you have a fire science or EMS degree, list it here and mention key coursework only if it adds something the certifications do not.</p>

        <h3>Skills</h3>
        <p>Keep this short and specific: languages spoken, mechanical skills, driving endorsements, technical rescue or wildland exposure, computer or reporting systems you have used. Avoid generic lists such as "hard worker" or "team player" — show those in your experience bullets instead.</p>

        <h3>Volunteer and community experience</h3>
        <p>Volunteer firefighting, explorer or cadet programs, coaching, community service and church or nonprofit work all speak to service and commitment. Treat them like jobs: organization, role, dates and one or two bullets on what you did.</p>
      </section>

      <section>
        <h2>An example layout</h2>
        <ol>
          <li>Header with contact information.</li>
          <li>Two- or three-line summary.</li>
          <li>Certifications and licenses.</li>
          <li>Experience (most recent first).</li>
          <li>Education and training.</li>
          <li>Skills.</li>
          <li>Volunteer and community involvement.</li>
        </ol>
        <p>If your most relevant asset is experience (for example, several years as a paramedic), you can place Experience above Certifications. Put your strongest section near the top.</p>
      </section>

      <section>
        <h2>Common mistakes</h2>
        <ul>
          <li><strong>Listing certifications you do not hold</strong> or hiding expirations. Background investigators check.</li>
          <li><strong>Vague duties instead of actions.</strong> "Responsible for" tells the reader little.</li>
          <li><strong>Unexplained gaps</strong> with no context. A short line ("Full-time EMT school, 2024") is better than a hole.</li>
          <li><strong>Dense formatting:</strong> tiny fonts, no white space, three columns. Panels read fast; make it easy.</li>
          <li><strong>Typos and inconsistent dates.</strong> Attention to detail is part of the job. Have someone else proofread.</li>
          <li><strong>Objective statements that only describe what you want.</strong> Lead with what you offer.</li>
          <li><strong>One resume for every department.</strong> Adjust the summary and emphasize what each department's announcement asks for.</li>
        </ul>
      </section>

      <section>
        <h2>Formatting tips</h2>
        <ul>
          <li>One page for most entry-level candidates; clean, readable font at 10.5–12 pt.</li>
          <li>Consistent tense: past tense for previous roles, present for current ones.</li>
          <li>Save and submit as a PDF unless the department requires another format, and name the file with your name.</li>
          <li>Match the dates and job titles on your resume exactly to what you write on the application and background packet.</li>
        </ul>
      </section>

      <section>
        <h2>Building it in First Step</h2>
        <p>The <Link to="/resume">Resume Builder</Link> is designed around the sections above. You can type or dictate your background, organize it into a fire-service layout, edit each section and preview five templates with a free account. Premium members can download a one-page PDF and request an AI review that flags weak bullets and missing details. Once your resume is ready, use it to rehearse the "tell us about yourself" question in our <Link to="/how-to-prepare-firefighter-interview">interview preparation guide</Link>.</p>
      </section>
    </SeoPageShell>
  );
}
